import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { PHONE_PREVIEW_ASPECT_RATIO } from '@util/bannerDimensions';
import BaseModal from './BaseModal';

// GB #1799 -- copy approved verbatim by Bill 2026-09-02. The title and
// buttons were approved the night before ("A, copy approved as written").
// The body statistic was independently fact-checked afterward (the
// original "65% of visits" claim was found false -- see this file's git
// history) and Bill re-ruled on the corrected body: "More than half of
// GearFocus purchases are made on a phone." Basis for that figure:
// 58.1% of purchases on mobile, 90d ending 2026-09-01; 52.0% at 30d --
// GA4 property 300639795, measured 2026-09-02. RE-VERIFY BEFORE CHANGING
// THIS SENTENCE -- this metric moves with the window measured (7d/30d/90d
// have disagreed by 10+ points before) and is not re-checked automatically.
// Every visible string lives here as a single source of truth, not
// scattered through JSX.
export const MOBILE_BANNER_CHOICE_COPY = {
  title: 'See how buyers on phones will see this',
  body: "More than half of GearFocus purchases are made on a phone. Right now they'll see the middle of this banner, cropped like this.",
  addButton: 'Add a phone-specific banner',
  keepButton: 'This is fine, keep it',
};

interface MobileBannerChoiceModalProps {
  isOpen: boolean;
  previewSrc: string;
  onAddMobileBanner: () => void;
  onKeepDesktopImage: () => void;
}

// GB #1799: fires once, right after a seller's desktop-banner upload
// succeeds, ONLY when they have no mobile banner set. Never mandatory --
// both buttons dismiss the modal, "keep it" just doesn't open the mobile
// file picker. The desktop banner is already saved by this point (the
// upload's own write already completed in runBannerUpload), so this is a
// choice-capturing interstitial, not a save gate.
//
// GB #1799 -- adversarial review: `disableBackdropDismiss` + `hideCloseIcon`
// suppress backdrop-click and the X icon, but NOT Escape (BaseModal's
// underlying Headless UI Dialog handles Escape regardless of that prop).
// That's fine, not a bug -- Escape is a legitimate keyboard "no," the same
// real outcome as clicking "keep it," and it can't be pressed before the
// desktop banner's own save has already completed (see above). NOTE this
// outcome is now persistent for the rest of the page session (Bill's
// decline-memory ruling, ShopSettingsPage.tsx) -- a reflexive Escape
// counts as a real decline and silences the modal until the seller
// reloads, same as clicking "keep it" deliberately would.
function MobileBannerChoiceModal({
  isOpen,
  previewSrc,
  onAddMobileBanner,
  onKeepDesktopImage,
}: MobileBannerChoiceModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      hideCloseIcon
      width="36rem"
      disableBackdropDismiss
      dismiss={onKeepDesktopImage}
    >
      <div className="flex w-full max-w-[36rem] flex-col items-center gap-[2.4rem] rounded-[1rem]">
        <div className="flex flex-col items-center gap-[0.8rem]">
          <h2 className="text-center text-[2.4rem] font-semibold leading-[3.2rem] text-brand-light-black">
            {MOBILE_BANNER_CHOICE_COPY.title}
          </h2>
          <span className="text-center leading-[2.2rem] text-brand-gray">
            {MOBILE_BANNER_CHOICE_COPY.body}
          </span>
        </div>

        <div
          className="relative w-full max-w-[280px] overflow-hidden rounded-[10px] bg-brand-lighter-gray"
          style={{ aspectRatio: String(PHONE_PREVIEW_ASPECT_RATIO) }}
        >
          <SafeImage
            className="h-full w-full object-cover"
            alt="Your banner cropped as it appears on a phone"
            fill
            key={previewSrc}
            src={previewSrc}
          />
        </div>

        <div className="flex w-full flex-col items-center gap-[1.2rem]">
          <Button
            type="primary"
            text={MOBILE_BANNER_CHOICE_COPY.addButton}
            onClick={onAddMobileBanner}
          />
          <Button
            type="text"
            text={MOBILE_BANNER_CHOICE_COPY.keepButton}
            onClick={onKeepDesktopImage}
          />
        </div>
      </div>
    </BaseModal>
  );
}

export default MobileBannerChoiceModal;
